import { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { Form, Flex, Typography } from "antd";
import { _isRequired, _isNumber, _greaterThan } from "utils/form/simple-validation";
import { useStores } from "stores";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import { NumberInput } from "ui";
import { AmlAlert } from "components";
import { getCryptoRates } from "services/settingsService";
import dayjs from "dayjs";
import { RATE_DEVIATION_WARNING_THRESHOLD } from "utils/envConstants";

interface IProps {
  name: string;
  cryptoSymbol?: "USDT" | "USDC";
  hidden?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export const UsdtRateInput = observer((props: IProps) => {
  const { settings } = useStores();

  const [usdtMarketRate, setUsdtMarketRate] = useState<number>(0);
  const [isRateFieldBlurred, setIsRateFieldBlurred] = useState(false);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    getCryptoRates({
      cryptoSymbol: props.cryptoSymbol || "USDT",
      fiatSymbol: "RUB",
      dateFrom: dayjs().format("YYYY-MM-DD"),
      dateTo: dayjs().format("YYYY-MM-DD"),
    })
      .then((response) => {
        if (cancelled) return;
        // Если курс найден, то используем его, иначе используем курс из настроек
        const rate = response.content?.[0]?.rate ?? settings.rates?.rates?.USDT ?? 0;

        setUsdtMarketRate(rate);
      })
      .catch(() => {
        if (cancelled) return;

        // Если ошибка, то используем курс из настроек
        setUsdtMarketRate(settings.rates?.rates?.USDT ?? 0);
      });

    return () => {
      cancelled = true;
    };
  }, [settings.rates?.rates?.USDT]);

  const form = Form.useFormInstance();
  const itemRateValue = Form.useWatch<number>(props.name, form);

  const rateDeviation = useMemo(() => {
    if (!itemRateValue || !usdtMarketRate) return 0;

    return Math.abs((itemRateValue - usdtMarketRate) / usdtMarketRate) * 100;
  }, [itemRateValue, usdtMarketRate]);

  const showDeviationWarning = isRateFieldBlurred && rateDeviation > RATE_DEVIATION_WARNING_THRESHOLD;

  return (
    <Form.Item
      name={props.name}
      rules={[_isRequired("Поле обязательно для ввода"), _isNumber(), _greaterThan(0)]}
      validateDebounce={FORM_VALIDATION_DEBOUNCE}
      hidden={props.hidden}
    >
      <Flex vertical gap="small">
        <Typography.Text type="secondary">
          Индикативный курс: {usdtMarketRate ? `${usdtMarketRate.toFixed(2)} руб.` : "загрузка…"}
        </Typography.Text>
        <NumberInput
          onFocus={() => {
            if (blurTimeoutRef.current) {
              clearTimeout(blurTimeoutRef.current);
            }
            setIsRateFieldBlurred(false);
          }}
          // задержка нужна чтобы страница не прыгала при клике на кнопки
          onBlur={() => {
            if (blurTimeoutRef.current) {
              clearTimeout(blurTimeoutRef.current);
            }
            blurTimeoutRef.current = setTimeout(() => setIsRateFieldBlurred(true), 300);
          }}
          placeholder={props.placeholder}
          disabled={props.disabled}
          maxDecimals={2}
        />
        {showDeviationWarning && (
          <AmlAlert
            className="align-items-center"
            amlMessage={{
              message: `Введенное значение значительно отличается от текущего рыночного курса. Проверьте правильность ввода.`,
              type: "warning",
            }}
          />
        )}
      </Flex>
    </Form.Item>
  );
});
